/**
 * Job Docs feature gate for OperatorView (the ShopPulse host).
 *
 * The feature is enabled only when ALL of the following agree (ANDed, so each is
 * an independent OFF switch and none force-enables on its own):
 *
 *  1. `envEnabled` — the build/env default (`JOB_DOCS_ENABLED`) marks the feature
 *     as wired for this host (dev/prod/staging). Hosts that don't ship it stay off.
 *  2. The `JobDocsDelivery` LaunchDarkly flag is the **authoritative live switch
 *     and defaults OFF**: it must resolve to a literal boolean `true` to enable.
 *     An absent or non-boolean flag (LD outage, pre-bootstrap, unprovisioned) is
 *     treated as disabled — the feature stays off until LD is explicitly turned
 *     on for the company, rather than defaulting open.
 *  3. Per-company MES settings: `undefined`/absent count as enabled (a not-yet-
 *     deployed backend doesn't hide the feature) — only an explicit `false` on
 *     either setting disables.
 *
 * So: wired host AND LD `true` AND no MES `false`. LD `false`, an unprovisioned
 * LD flag, an unwired host, or an explicit company `false` each disable it.
 *
 * Flag key MUST stay in sync with the other hosts.
 */
export const JOB_DOCS_DELIVERY_FLAG = 'JobDocsDelivery';

/**
 * @param {Object} args
 * @param {Record<string, unknown>|null|undefined} args.ldFlags LaunchDarkly flags
 *   (raw keys — OperatorView sets `useCamelCaseFlagKeys: false`).
 * @param {boolean} args.envEnabled Build/env default (`JOB_DOCS_ENABLED`) — whether
 *   the feature is wired for this host. Must be true for the feature to enable.
 * @param {{ jobDocsEnabled?: boolean, jobDocsShoppulseEnabled?: boolean }|null|undefined} args.companySettings
 * @returns {boolean}
 */
export function isJobDocsFeatureEnabled({
  ldFlags,
  envEnabled,
  companySettings,
}) {
  // LD is the authoritative switch and defaults OFF: only a literal `true`
  // enables. Absent/non-boolean (outage, pre-bootstrap, unprovisioned) → off.
  const ldEnabled = ldFlags?.[JOB_DOCS_DELIVERY_FLAG] === true;

  // companySettings undefined/absent means "not yet deployed" — only an explicit
  // false disables. Boolean()-wrapped so the return honors the boolean contract
  // even if `envEnabled` is passed a non-boolean (the App path coerces it).
  return Boolean(
    envEnabled &&
      ldEnabled &&
      companySettings?.jobDocsEnabled !== false &&
      companySettings?.jobDocsShoppulseEnabled !== false
  );
}
